import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ETestIds } from 'shared/constants/testIds';
import { selectCalculatorErrors } from 'shared/selectors/landing';
import { useIsDesktop } from '../../../../hooks/common';

import { computeStickyBarPhase, TRedesignStickyBarPhase } from '../utils/computeStickyBarPhase';
import { getOffersAnchorElement } from '../utils/getOffersAnchorElement';
import { resolveStickyBarVariant, TRedesignStickyBarVariant } from '../utils/resolveStickyBarVariant';
import { useResetButtonVisibility } from './useResetButtonVisibility';

const BODY_PADDING_BOTTOM = '120px';

interface IUseRedesignStickyBarStateParams {
  hasOffers: boolean;
  isFetching: boolean;
  enabled: boolean;
}

export const useRedesignStickyBarState = ({
  hasOffers,
  isFetching,
  enabled,
}: IUseRedesignStickyBarStateParams): TRedesignStickyBarVariant => {
  const isDesktop = useIsDesktop();
  const calculatorErrors = useSelector(selectCalculatorErrors);
  const hasCalculatorErrors = calculatorErrors.length > 0;
  const [phase, setPhase] = useState<TRedesignStickyBarPhase>('beforeOffers');
  const isResetButtonVisible = useResetButtonVisibility(enabled, hasOffers, isFetching);

  const updatePhase = useCallback(() => {
    const listing = document.querySelector(`[data-testid=${ETestIds.OffersListing}]`);

    if (!listing) {
      setPhase('beforeOffers');

      return;
    }

    const offersAnchor = getOffersAnchorElement();

    setPhase(
      computeStickyBarPhase(
        listing.getBoundingClientRect(),
        window.innerHeight,
        offersAnchor?.getBoundingClientRect(),
        window.scrollY,
      ),
    );
  }, []);

  useEffect(() => {
    if (isDesktop || !enabled) {
      return;
    }

    updatePhase();

    const handleUpdate = () => {
      updatePhase();
    };

    window.addEventListener('scroll', handleUpdate, { passive: true });
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [enabled, isDesktop, updatePhase, hasOffers, isFetching]);

  const variant = resolveStickyBarVariant({
    phase,
    hasOffers,
    isResetButtonVisible,
    hasCalculatorErrors,
    isFetching,
  });

  useEffect(() => {
    if (isDesktop || !enabled) {
      return;
    }

    const documentBody = document.body as HTMLBodyElement;

    if (variant === 'hidden') {
      documentBody.style.paddingBottom = '';
    } else {
      documentBody.style.paddingBottom = BODY_PADDING_BOTTOM;
    }

    return () => {
      documentBody.style.paddingBottom = '';
    };
  }, [enabled, isDesktop, variant]);

  return variant;
};
